const WHATSAPP_URL = 'https://wa.me/972559116975';

/**
 * Pricing grid + buy buttons for the individual classes landing page.
 */
export default function IndividualPricingSection({
  sectionRef,
  revealRef,
  visible,
  passTypes,
  loading,
  singlePrice,
  showRulesCheckbox,
  rulesAgreedChecked,
  setRulesAgreedChecked,
  onOpenPolicy,
  purchasingId,
  onPurchase,
  user,
  details,
  t,
  sessionLabel,
}) {
  const onlySingleLessonPass =
    !loading &&
    passTypes.length === 1 &&
    (passTypes[0]?.classesIncluded || 1) === 1;

  return (
    <section ref={sectionRef} className="py-24 md:py-32 px-6 md:px-20 bg-warm-white">
      <div
        ref={revealRef}
        className={`max-w-6xl mx-auto transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 mb-12 md:mb-16">
          <div>
            <div className="flex items-center gap-3 text-eyebrow font-sans uppercase tracking-[0.18em] text-clay mb-4">
              <span className="w-6 h-px bg-clay flex-shrink-0" />
              {t.pricingLabel}
            </div>
            <h2 className="font-serif font-medium leading-tight text-ink" style={{ fontSize: 'clamp(32px, 4.5vw, 52px)' }}>
              {t.pricingTitle}
              <em className="italic text-clay">{t.pricingTitleEm}</em>
            </h2>
          </div>
          {!onlySingleLessonPass && (
            <p className="text-sm font-sans font-light text-ink-light max-w-[300px] md:text-right leading-relaxed border-l-2 md:border-l-0 md:border-r-2 border-clay/25 pl-4 md:pl-0 md:pr-4">
              {t.pricingNote}
            </p>
          )}
        </div>

        {showRulesCheckbox && (
          <div className="mb-8 flex justify-center">
            <label className="flex items-start gap-3 text-left cursor-pointer max-w-xl p-4 rounded-xl bg-cream/50 border border-sand-dark/40">
              <input
                type="checkbox"
                checked={rulesAgreedChecked}
                onChange={(e) => setRulesAgreedChecked(e.target.checked)}
                className="mt-1 rounded border-sand-dark text-clay focus:ring-clay"
              />
              <span className="text-sm font-sans font-light text-ink-mid">
                {details.firstPassRulesLabelPrefix}
                <button type="button" onClick={onOpenPolicy} className="text-clay hover:underline font-medium">
                  {details.firstPassRulesLinkText}
                </button>
              </span>
            </label>
          </div>
        )}

        {loading ? (
          <p className="text-center text-ink-mid font-sans font-light py-16">{details.loadingDotDotDot}</p>
        ) : passTypes.length === 0 ? (
          <p className="text-center text-ink-mid font-sans font-light py-16">{details.noClasses}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {passTypes.map((pass) => {
              const name = pass.name || '';
              const displayName = details[name] || pass.displayName || name;
              const price = pass.basePrice ?? 0;
              const currency = pass.currency === 'ILS' ? '₪' : pass.currency || '₪';
              const n = pass.classesIncluded || 1;
              const perSession = Math.round(price / n);
              const saveAmount = n > 1 ? singlePrice * n - price : 0;
              const isBest = n === 8;
              const isPurchasing = purchasingId === pass._id;
              let savingText = '';
              if (saveAmount > 0) {
                if (isBest) savingText = `${t.popularChoice} · ${t.saving} ${saveAmount}₪`;
                else if (n === 12) savingText = `${t.maxSaving} · ${saveAmount}₪`;
                else savingText = `${t.saving} ${saveAmount}₪`;
              }
              return (
                <div
                  key={pass._id}
                  className={`flex flex-col rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${
                    isBest
                      ? 'bg-gradient-to-b from-clay to-clay-dark text-white ring-2 ring-clay/30 ring-offset-4 ring-offset-warm-white shadow-xl'
                      : 'bg-white border border-sand-dark/50 shadow-lg hover:border-clay/20'
                  }`}
                >
                  <div className="p-7 md:p-8 flex flex-col flex-1">
                    <div className={`text-[11px] font-sans font-normal tracking-wider uppercase flex items-baseline gap-2 mb-4 ${isBest ? 'text-white/65' : 'text-ink-light'}`}>
                      <span className={`font-serif text-4xl font-normal leading-none ${isBest ? 'text-white' : 'text-clay'}`}>{n}</span>
                      <span>{sessionLabel(n)}</span>
                    </div>
                    <div className={`font-serif text-lg font-medium leading-tight mb-2 ${isBest ? 'text-white' : 'text-ink'}`}>{displayName}</div>
                    <div className={`text-xs font-sans font-light mb-6 ${isBest ? 'text-white/60' : 'text-ink-light'}`}>{details.individualClassesValidDays}</div>
                    <div
                      className={`font-serif text-4xl md:text-[2.75rem] font-normal leading-none ${n > 1 ? 'mb-1' : 'mb-5'} ${isBest ? 'text-white' : 'text-clay'}`}
                    >
                      {price}
                      <span className={`text-lg font-sans font-light ml-0.5 ${isBest ? 'text-white/65' : 'text-ink-light'}`}>{currency}</span>
                    </div>
                    {n > 1 && (
                      <div className={`text-xs font-sans font-light mb-5 ${isBest ? 'text-white/65' : 'text-ink-light'}`}>
                        <span className={isBest ? 'text-white font-medium' : 'text-clay font-medium'}>{perSession}₪</span>
                        {` ${t.perSession}`}
                      </div>
                    )}
                    {savingText ? (
                      <div className={`inline-block text-[10px] font-medium tracking-wide py-1.5 px-2.5 rounded-md mb-5 ${isBest ? 'bg-white/20 text-white' : 'bg-cream text-clay'}`}>
                        {savingText}
                      </div>
                    ) : (
                      <div className="h-8 mb-5" />
                    )}
                    <button
                      type="button"
                      onClick={() => onPurchase(pass)}
                      disabled={isPurchasing}
                      className={`mt-auto w-full py-3.5 rounded-lg font-sans text-[12px] font-semibold tracking-wider uppercase transition-all disabled:opacity-60 ${
                        isBest ? 'bg-white text-clay-dark hover:bg-cream shadow-md' : 'bg-ink text-white hover:bg-[#3a2e2a]'
                      }`}
                    >
                      {!user ? t.loginToBuy : isPurchasing ? details.loadingDotDotDot : t.buy}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-col md:flex-row md:justify-between gap-6 mt-10 pt-10 border-t border-sand-dark/40">
          <p className="text-sm font-sans font-light text-ink-light leading-relaxed max-w-md">
            {t.pricingFooter1}
            <a href={WHATSAPP_URL} className="text-clay font-medium hover:underline">
              {t.ctaButton}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
